'use client';

import { faCheck, faRotateRight, faTriangleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Spinner, Tooltip } from '@ui';
import Dot from '@ui/Dot';
import { useTranslation } from 'react-i18next';
import { cn } from '~/lib/cn';
import type { CaptureDocument, CaptureDocumentStatus } from './types';

/** Colour per state, so a stack reads at a glance instead of word by word. */
const DOT_COLOR: Record<CaptureDocumentStatus, 'primary' | 'secondary' | 'success' | 'warning' | 'danger'> = {
    pending: 'secondary',
    processing: 'primary',
    ready: 'success',
    failed: 'danger',
    discarded: 'secondary',
    confirmed: 'success'
};

interface Props {
    documents: CaptureDocument[];
    /** Files handed over but not yet acknowledged by the backend. */
    pendingUploads: { uid: string; name: string }[];
    activeId: number | null;
    onSelect: (documentId: number) => void;
    onDiscard: (documentId: number) => void;
    onRetry: (documentId: number) => void;
}

const label = (document: CaptureDocument, index: number) =>
    document.original_name?.replace(/\.[^.]+$/, '') || `#${index + 1}`;

/**
 * The stack: one card per document, in arrival order.
 *
 * A plain card list rather than a DataTable — that one requires a fetchUrl and renders a server
 * paginator, and these documents live in memory and change over a websocket.
 */
const BelegQueue = ({ documents, pendingUploads, activeId, onSelect, onDiscard, onRetry }: Props) => {
    const { t } = useTranslation('invoice');

    const open = documents.filter((document) => 'discarded' !== document.status);

    return (
        <div className="tw-flex tw-max-h-[50%] tw-shrink-0 tw-flex-col tw-gap-2 tw-overflow-y-auto tw-pr-1">
            {open.map((document, index) => {
                const isActive = document.id === activeId;
                const isBusy = 'pending' === document.status || 'processing' === document.status;
                const isConfirmed = 'confirmed' === document.status;

                return (
                    <div
                        key={document.id}
                        className={cn(
                            'tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-solid tw-p-2 tw-transition',
                            isActive
                                ? 'tw-border-primary tw-bg-primary/5'
                                : 'tw-border-accents-2 hover:tw-border-accents-4',
                            isConfirmed && 'tw-opacity-60'
                        )}
                    >
                        <button
                            type="button"
                            onClick={() => onSelect(document.id)}
                            className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-3 tw-border-0 tw-bg-transparent tw-p-0 tw-text-left"
                        >
                            {isBusy ? <Spinner size="xs" /> : <Dot size="md" color={DOT_COLOR[document.status]} />}

                            <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
                                <span className="tw-truncate tw-text-xs tw-font-semibold tw-text-accents-9">
                                    {label(document, index)}
                                </span>
                                <span className="tw-truncate tw-text-[11px] tw-text-accents-6">
                                    {t(`belegAssistant.status.${document.status}`)}
                                    {document.page_count && document.page_count > 1
                                        ? ` · ${t('belegAssistant.queue.pages', { count: document.page_count })}`
                                        : ''}
                                </span>
                            </span>
                        </button>

                        {isConfirmed && <FontAwesomeIcon icon={faCheck} className="tw-text-success" size="sm" />}

                        {'failed' === document.status && (
                            <Tooltip
                                tooltip={t(`belegAssistant.errors.${document.error_code ?? 'ocr_failed'}`)}
                                placement="left"
                            >
                                <FontAwesomeIcon icon={faTriangleExclamation} className="tw-text-error" size="sm" />
                            </Tooltip>
                        )}

                        <span className="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
                            {'failed' === document.status && (
                                <Tooltip tooltip={t('belegAssistant.actions.retry')}>
                                    <Button
                                        size="xs"
                                        color="light"
                                        flat
                                        onPress={() => onRetry(document.id)}
                                        icon={<FontAwesomeIcon icon={faRotateRight} />}
                                    />
                                </Tooltip>
                            )}
                            {!isConfirmed && (
                                <Tooltip tooltip={t('belegAssistant.actions.discard')}>
                                    <Button
                                        size="xs"
                                        color="light"
                                        flat
                                        onPress={() => onDiscard(document.id)}
                                        icon={<FontAwesomeIcon icon={faXmark} />}
                                    />
                                </Tooltip>
                            )}
                        </span>
                    </div>
                );
            })}

            {pendingUploads.map((upload) => (
                <div
                    key={upload.uid}
                    className="tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-dashed tw-border-accents-3 tw-p-2"
                >
                    <Spinner size="xs" />
                    <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
                        <span className="tw-truncate tw-text-xs tw-font-semibold tw-text-accents-7">{upload.name}</span>
                        <span className="tw-text-[11px] tw-text-accents-6">
                            {t('belegAssistant.status.uploading')}
                        </span>
                    </span>
                </div>
            ))}
        </div>
    );
};

export default BelegQueue;
